import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Log, Subject, LogStatus } from '../../types/log';

export type LogManagerViewMode = 'calendar' | 'list';

export interface LogFilter {
  status?: LogStatus;
  category?: string;
  subjectId?: string;
  startTimeFrom?: number;
  startTimeTo?: number;
}

interface LogManagerState {
  logs: Log[];
  subjects: Subject[];
  selectedLogId: string | null;
  selectedSubjectIds: string[];
  viewMode: LogManagerViewMode;
  filter: LogFilter;
  loading: boolean;
  error: string | null;
}

const initialState: LogManagerState = {
  logs: [],
  subjects: [],
  selectedLogId: null,
  selectedSubjectIds: [],
  viewMode: 'calendar',
  filter: {},
  loading: false,
  error: null,
};

const logSlice = createSlice({
  name: 'logManager',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setLogs(state, action: PayloadAction<Log[]>) {
      state.logs = action.payload;
      state.loading = false;
    },
    addLog(state, action: PayloadAction<Log>) {
      state.logs.unshift(action.payload);
    },
    updateLog(state, action: PayloadAction<Log>) {
      const index = state.logs.findIndex((l) => l.id === action.payload.id);
      if (index !== -1) {
        state.logs[index] = action.payload;
      }
    },
    removeLog(state, action: PayloadAction<string>) {
      state.logs = state.logs.filter((l) => l.id !== action.payload);
      if (state.selectedLogId === action.payload) {
        state.selectedLogId = null;
      }
    },
    selectLog(state, action: PayloadAction<string | null>) {
      state.selectedLogId = action.payload;
    },
    setViewMode(state, action: PayloadAction<LogManagerViewMode>) {
      state.viewMode = action.payload;
    },
    setFilter(state, action: PayloadAction<LogFilter>) {
      state.filter = action.payload;
    },
    clearFilter(state) {
      state.filter = {};
    },
    setSubjects(state, action: PayloadAction<Subject[]>) {
      state.subjects = action.payload;
    },
    addSubject(state, action: PayloadAction<Subject>) {
      state.subjects.unshift(action.payload);
    },
    updateSubject(state, action: PayloadAction<Subject>) {
      const index = state.subjects.findIndex((s) => s.id === action.payload.id);
      if (index !== -1) {
        state.subjects[index] = action.payload;
      }
    },
    removeSubject(state, action: PayloadAction<string>) {
      state.subjects = state.subjects.filter((s) => s.id !== action.payload);
      state.selectedSubjectIds = state.selectedSubjectIds.filter(id => id !== action.payload);
    },
    selectSubject(state, action: PayloadAction<string | null>) {
      if (action.payload === null) {
        state.selectedSubjectIds = [];
        state.filter = { ...state.filter, subjectId: undefined };
      } else {
        const index = state.selectedSubjectIds.indexOf(action.payload);
        if (index >= 0) {
          state.selectedSubjectIds.splice(index, 1);
        } else {
          state.selectedSubjectIds.push(action.payload);
        }
        const firstSubjectId = state.selectedSubjectIds[0] || undefined;
        state.filter = { ...state.filter, subjectId: firstSubjectId };
      }
    },
    clearSelectedSubjects(state) {
      state.selectedSubjectIds = [];
      state.filter = { ...state.filter, subjectId: undefined };
    },
  },
});

export const {
  setLoading,
  setError,
  setLogs,
  addLog,
  updateLog,
  removeLog,
  selectLog,
  setViewMode,
  setFilter,
  clearFilter,
  setSubjects,
  addSubject,
  updateSubject,
  removeSubject,
  selectSubject,
  clearSelectedSubjects,
} = logSlice.actions;

export default logSlice.reducer;